import { Link } from 'react-router-dom'
import { useResource } from '../api/hooks'
import type { AccessEvent, AccessSummary, CountResult, CursorPage, DoorListItem } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Alert, Card, ErrorBox, Spinner, formatTime, relative } from '../components/Ui'
import { de, fmt } from '../i18n/de'

/** Firmware-Versionen, die Payload-Format 2 verstehen. */
// Tuerformat-Mindestversion. 0.0.0 heisst: keine Tuer gilt als zu alt - der erste Stand mit
// Nutzlastformat 2 ist 1.0.0, und davor war nie eine Firmware im Feld. Verlangt ein
// kuenftiges Format mehr, wird dieser Wert angehoben; erst dann hat die Warnung wieder eine
// Aussage. Eine Warnung, die immer leuchtet, wird ignoriert.
const MIN_FIRMWARE = '0.0.0'

export function DashboardPage() {
  const doors = useResource<DoorListItem[]>('/doors')
  const summary = useResource<AccessSummary>('/accessevents/summary')
  const recent = useResource<CursorPage<AccessEvent>>('/accessevents?limit=6')
  const expiring = useResource<CountResult>('/doorcodes/count?expiringWithinDays=14')
  const total = useResource<CountResult>('/doorcodes/count?status=all')

  if (doors.error) return <ErrorBox error={doors.error} onRetry={doors.reload} />

  const doorList = doors.data ?? []
  const live = doorList.filter(door => door.status === 'live').length
  const stale = doorList.filter(door => door.status === 'stale')
  const outdated = doorList.filter(door =>
    door.firmwareVersion !== null && isOlder(door.firmwareVersion, MIN_FIRMWARE))

  return (
    <>
      <PageHeader title={de.dashboard.title}
                  subtitle={new Date().toLocaleDateString('de-AT', { dateStyle: 'full' })} />

      <div className="grid cols-4" style={{ marginBottom: 'var(--sp-4)' }}>
        <Stat label={de.dashboard.doorCodes}
              value={total.data ? String(total.data.count) : '…'}
              note={expiring.data
                ? fmt(de.dashboard.expiringSoon, { count: expiring.data.count })
                : ''} />

        <Stat label={de.dashboard.doorsOnline}
              value={`${live} / ${doorList.length}`}
              note={stale.length > 0
                ? `${stale.map(d => d.name).join(', ')}`
                : ''}
              noteWarning={stale.length > 0} />

        <Stat label={de.dashboard.accessesToday}
              value={summary.data ? String(summary.data.granted) : '…'}
              note={summary.data ? sparkline(summary.data.hourlyGranted) : ''} />

        <Stat label={de.dashboard.denied}
              value={summary.data ? String(summary.data.denied) : '…'}
              note={summary.data
                ? Object.entries(summary.data.deniedByReason)
                    .map(([reason, count]) => `${count}× ${reasonText(reason)}`).join(' · ')
                : ''} />
      </div>

      {/* Die wichtigste Information der Anlage: hier steht, ob eine gerade vorgenommene
          Sperre überhaupt wirkt. Deshalb kein kleines Symbol. */}
      {stale.map(door => (
        <div key={door.id} style={{ marginBottom: 'var(--sp-3)' }}>
          <Alert kind="warning"
                 actions={<Link className="btn btn-ghost" to={`/doors/${door.id}`}>Details</Link>}>
            <strong>{fmt(de.dashboard.staleWarning, {
              name: door.name,
              since: relative(door.lastSeenAt),
            })}</strong>
            <br />
            {de.dashboard.staleConsequence}
          </Alert>
        </div>
      ))}

      {outdated.map(door => (
        <div key={door.id} style={{ marginBottom: 'var(--sp-3)' }}>
          {/* Kein Aktualisieren-Knopf: die Firmware aktualisiert sich selbst, ein
              vorgetäuschter Auslöser wäre irreführend. */}
          <Alert kind="info">
            <strong>{fmt(de.dashboard.firmwareWarning, {
              name: door.name,
              version: door.firmwareVersion ?? '?',
            })}</strong>
            <br />
            {de.dashboard.firmwareConsequence}
          </Alert>
        </div>
      ))}

      {(summary.data?.unknownCodeAttempts.length ?? 0) > 0 && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert kind="warning">
            {summary.data!.unknownCodeAttempts.map(attempt => (
              <div key={attempt.doorID}>
                {fmt(de.dashboard.unknownCodes, {
                  count: attempt.count,
                  door: attempt.doorName,
                })}
              </div>
            ))}
          </Alert>
        </div>
      )}

      <Card title={de.dashboard.lastAccesses}
            actions={<Link className="btn btn-ghost" to="/log">{de.dashboard.showLog}</Link>}>
        {recent.loading && <Spinner />}
        {recent.error ? <ErrorBox error={recent.error} onRetry={recent.reload} /> : null}

        {recent.data && recent.data.items.length === 0 && (
          <div className="faint sm">{de.app.noEntries}</div>
        )}

        {recent.data && recent.data.items.length > 0 && (
          <table>
            <tbody>
              {recent.data.items.map(event => (
                <tr key={event.id}>
                  <td className="mono faint" style={{ width: 90 }}>{formatTime(event.occurredAt)}</td>
                  <td>{event.doorName}</td>
                  <td>{event.doorCodeName ?? <span className="faint">{de.log.unknownCode}</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {event.granted
                      ? <span className="status status-ok">✓ {de.log.granted}</span>
                      : <span className="status" style={{ color: 'var(--danger)' }}>✗ {de.log.denied}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

function Stat({ label, value, note, noteWarning }: {
  label: string
  value: string
  note: string
  noteWarning?: boolean
}) {
  return (
    <div className="card">
      <div className="card-b stat">
        <div className="label">{label}</div>
        <div className="value">{value}</div>
        <div className="note" style={noteWarning ? { color: 'var(--warning)' } : undefined}>
          {note || ' '}
        </div>
      </div>
    </div>
  )
}

function sparkline(values: number[]): string {
  const bars = '▁▂▃▄▅▆▇█'
  const max = Math.max(...values, 1)
  return values
    .filter((_, hour) => hour >= 5 && hour <= 22)
    .map(value => bars[Math.min(bars.length - 1, Math.round((value / max) * (bars.length - 1)))])
    .join('')
}

function reasonText(reason: string): string {
  switch (reason) {
    case 'unknown_code': return 'unbekannt'
    case 'outside_schedule': return 'Zeitplan'
    case 'outside_date_range': return 'Zeitraum'
    case 'disabled': return 'gesperrt'
    case 'no_access_to_door': return 'keine Berechtigung'
    default: return reason
  }
}

/** Vergleicht Versionen der Form a.b.c; unparsbare Werte gelten als zu alt. */
function isOlder(version: string, minimum: string): boolean {
  const parse = (value: string) => value.split('.').map(part => Number.parseInt(part, 10) || 0)
  const left = parse(version)
  const right = parse(minimum)

  for (let index = 0; index < Math.max(left.length, right.length); index++) {
    const a = left[index] ?? 0
    const b = right[index] ?? 0
    if (a !== b) return a < b
  }
  return false
}
